import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  Edit,
  Loader2,
  ClipboardList,
} from 'lucide-react';
import { onboardingAPI, suppliersAPI, FilledDataset } from '../../lib/api';

// Status colors
const statusColors: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  submitted: 'bg-cyan-100 text-cyan-800',
  under_review: 'bg-purple-100 text-purple-800',
  more_info_required: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

// Status icons
const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'approved':
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case 'submitted':
    case 'under_review':
      return <Clock className="h-5 w-5 text-blue-600" />;
    case 'more_info_required':
    case 'rejected':
      return <AlertTriangle className="h-5 w-5 text-amber-600" />;
    default:
      return <FileText className="h-5 w-5 text-gray-600" />;
  }
};

export default function SupplierQuestionnaires() {
  const { id } = useParams();
  const navigate = useNavigate();
  const supplierId = Number(id);

  const [loading, setLoading] = useState(true);
  const [supplierName, setSupplierName] = useState('');
  const [questionnaires, setQuestionnaires] = useState<FilledDataset[]>([]);
  const [activeTab, setActiveTab] = useState('all');

  const fetchData = async () => {
    try {
      setLoading(true);

      // First fetch supplier profile to get the company ID
      const supplierRes = await suppliersAPI.getProfile(supplierId);
      setSupplierName(supplierRes.data.company_name);

      // Use company ID (not profile ID) for filled-datasets API
      const companyId = supplierRes.data.company;
      const questionnairesRes = await onboardingAPI.getFilledDatasets({ supplier: companyId });

      const filledDatasets = questionnairesRes.data.results || questionnairesRes.data || [];
      setQuestionnaires(filledDatasets);
    } catch (error) {
      console.error('Failed to fetch questionnaires:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (supplierId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  // Filter questionnaires by tab
  const filteredQuestionnaires = questionnaires.filter((q) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return ['not_started', 'in_progress', 'more_info_required'].includes(q.status);
    if (activeTab === 'submitted') return ['submitted', 'under_review'].includes(q.status);
    if (activeTab === 'completed') return q.status === 'approved';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/suppliers')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Onboarding Questionnaires</h1>
            <p className="text-muted-foreground">{supplierName}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">
            All
            <Badge variant="secondary" className="ml-2">
              {questionnaires.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="pending">
            Pending
            <Badge variant="secondary" className="ml-2">
              {questionnaires.filter((q) => ['not_started', 'in_progress', 'more_info_required'].includes(q.status)).length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="submitted">
            Submitted
            <Badge variant="secondary" className="ml-2">
              {questionnaires.filter((q) => ['submitted', 'under_review'].includes(q.status)).length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed
            <Badge variant="secondary" className="ml-2">
              {questionnaires.filter((q) => q.status === 'approved').length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredQuestionnaires.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No questionnaires found</h3>
              <p className="text-muted-foreground">
                {activeTab === 'all'
                  ? 'This supplier has no assigned questionnaires yet.'
                  : `No ${activeTab} questionnaires.`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredQuestionnaires.map((questionnaire) => (
                <Card
                  key={questionnaire.id}
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => {
                    // Navigate to fill or review based on status
                    if (['not_started', 'in_progress', 'more_info_required'].includes(questionnaire.status)) {
                      navigate(`/onboarding/fill/${questionnaire.id}`);
                    } else {
                      navigate(`/onboarding/review/${questionnaire.id}`);
                    }
                  }}
                >
                  <CardContent className="p-4 text-center space-y-3">
                    {/* Icon */}
                    <div className="flex justify-center">
                      <div className="w-24 h-24 border rounded-lg flex items-center justify-center bg-gray-50">
                        <FileText className="h-12 w-12 text-blue-500" />
                      </div>
                    </div>

                    {/* Status Badge */}
                    <Badge className={statusColors[questionnaire.status] || statusColors['not_started']}>
                      <StatusIcon status={questionnaire.status} />
                      <span className="ml-1">{questionnaire.status?.replace(/_/g, ' ')}</span>
                    </Badge>

                    {/* Progress (if in progress) */}
                    {questionnaire.status === 'in_progress' && questionnaire.completion_percentage !== undefined && (
                      <div className="space-y-1">
                        <Progress value={questionnaire.completion_percentage} className="h-2" />
                        <span className="text-xs text-muted-foreground">
                          {Math.round(questionnaire.completion_percentage)}% complete
                        </span>
                      </div>
                    )}

                    {/* Questionnaire Name */}
                    <div className="min-h-[45px] flex items-center justify-center">
                      <h3 className="text-sm font-semibold line-clamp-2">
                        {questionnaire.dataset_name || 'Questionnaire'}
                      </h3>
                    </div>

                    {/* Action Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (['not_started', 'in_progress', 'more_info_required'].includes(questionnaire.status)) {
                          navigate(`/onboarding/fill/${questionnaire.id}`);
                        } else {
                          navigate(`/onboarding/review/${questionnaire.id}`);
                        }
                      }}
                    >
                      {['not_started', 'in_progress', 'more_info_required'].includes(questionnaire.status) ? (
                        <>
                          <Edit className="h-4 w-4 mr-1" />
                          {questionnaire.status === 'not_started' ? 'Start' : 'Continue'}
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
